import { morphSection } from '@theme/section-renderer';

/**
 * TK fulfilment picker.
 *
 * The required delivery-or-pickup choice on the cart, plus the customer's
 * phone number on pickup orders (payment is taken after weighing, by the
 * card on file or by phone; delivery orders carry a phone on their checkout
 * address). Source of truth is cart
 * attributes: every change POSTs /cart/update.js with the section ids of all
 * mounted cart sections, then morphs them, so the Liquid-rendered summary
 * (delivery fee, estimated tax, total, checkout gate) always agrees with
 * the server. Drawer sections are morphed in hydration mode so an open
 * drawer stays open.
 *
 * The day strip is a small calendar built here, not in Liquid: pages are
 * cached, so "today" and cutoff comparisons need the browser clock, pinned
 * to America/Toronto. Unavailable days render dimmed with a reason, per the
 * approved design.
 *
 * Morph survival: section morphs replace this element's children in place
 * (no reconnect), so a MutationObserver re-renders the strip and re-reads
 * server state whenever an outside render lands.
 */

const TZ = 'America/Toronto';
const DAY_MS = 24 * 60 * 60 * 1000;

/** Theme is a top-level const in scripts.liquid — global lexical scope, not window. */
function theme() {
  // eslint-disable-next-line no-undef
  return typeof Theme !== 'undefined' ? Theme : {};
}

/** @returns {{iso: string, minutes: number}} now in store time */
function torontoNow() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date());
  const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
  const hour = get('hour') === '24' ? '00' : get('hour');
  return {
    iso: `${get('year')}-${get('month')}-${get('day')}`,
    minutes: parseInt(hour, 10) * 60 + parseInt(get('minute'), 10),
  };
}

/** @param {string} hhmm @returns {number|null} minutes since midnight */
function toMinutes(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec((hhmm || '').trim());
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
}

/** Formats cents in the shop's money style (falls back to plain $). */
function formatMoney(cents) {
  const format = theme().moneyFormat;
  const amount = (cents / 100).toFixed(2);
  if (format) return format.replace(/{{\s*amount[a-z_]*\s*}}/, amount);
  return `$${amount}`;
}

/** Digits of a North American number, without a leading country code 1. */
function phoneDigits(value) {
  const digits = String(value || '').replace(/\D+/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

/** A dialable 10-digit number: area code and exchange can't start with 0 or 1. */
function phoneValid(value) {
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(phoneDigits(value));
}

/** "4167895333" → "416-789-5333" (the way the counter writes it) */
function formatPhone(value) {
  const d = phoneDigits(value);
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

/** hydration keeps an open drawer open; full is right for the cart page */
function morphModeFor(sectionId) {
  const component = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
  return component instanceof HTMLElement && component.dataset.drawer !== undefined ? 'hydration' : 'full';
}

class TkFulfilment extends HTMLElement {
  /** @type {AbortController | null} */
  #listeners = null;
  /** @type {MutationObserver | null} */
  #observer = null;
  /** @type {MutationObserver | null} document-wide gate healer */
  #gateObserver = null;
  #applyGateScheduled = false;
  #rendering = false;
  #syncTimer = 0;
  #expiryTimer = 0;
  #gateTimer = 0;
  #debugTimer = 0;
  #debugNote = '';
  #syncQueued = false;
  #syncing = false;
  #syncPending = false;
  /**
   * Whether the server has acknowledged the current local state. Starts true
   * (the initial state IS the server's); any local change flips it false
   * until a sync returns 2xx. The checkout gate only opens on acked state,
   * so a failed write can never let an order through without its attributes.
   */
  #acked = true;

  /** True when a save failed at the moment of checkout; the hint says so. */
  #saveError = false;
  /** Monotonic sync counter used to ignore stale server-rendered state. */
  #nonce = 0;
  #lastSentNonce = 0;
  #failures = 0;
  #lastSignature = '';
  #skippedMorph = false;
  /** @type {Map<string, string>} iso → human day label */
  #dayByIso = new Map();

  connectedCallback() {
    // Seed the write counter from the cart's last acked nonce so the
    // stale-morph guard in refresh() is meaningful on a return visit.
    this.#nonce = parseInt(this.dataset.nonce || '0', 10) || 0;
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.#readServerState();
    for (const button of this.querySelectorAll('[data-tk-method]')) {
      button.addEventListener(
        'click',
        (event) => {
          // The drawer closes itself when a click's target is no longer
          // inside its panel; our re-renders replace nodes, so the click
          // must never bubble past the picker.
          event.stopPropagation();
          this.#selectMethod(button.dataset.tkMethod);
        },
        { signal }
      );
    }

    // Every enabled checkout click routes through here, acked or not. The
    // one job of this handler is that a click NEVER dies silently: flush any
    // pending save, then submit — and #submitCheckout re-resolves the button
    // from the live DOM (section morphs can replace it mid-flush) and falls
    // back to a plain navigation if the form path fails for any reason.
    document.addEventListener(
      'click',
      (event) => {
        if (event.defaultPrevented) return;
        const target = event.target instanceof Element ? event.target.closest('[data-tk-gated-checkout]') : null;
        if (!target) return;
        if (target.hasAttribute('disabled')) {
          // The pay control is an anchor: "disabled" is our attribute, not
          // the browser's, so its href (and keyboard Enter) must be blocked
          // here — with the same visible answer as any locked click.
          event.preventDefault();
          event.stopPropagation();
          this.#debugNote = 'click: locked control → nudge';
          this.#nudgeIncomplete();
          return;
        }
        if (!this.complete) {
          // An enabled button under an incomplete choice is a server render
          // the gate hasn't corrected yet. Never silent: answer like the
          // locked state and re-assert the gate.
          event.preventDefault();
          event.stopPropagation();
          this.#debugNote = 'click: incomplete → nudge';
          this.#nudgeIncomplete();
          this.#applyGate();
          return;
        }
        this.#debugNote = 'click: complete → checkout';
        event.preventDefault();
        event.stopPropagation();
        const go = () => {
          try {
            this.#goToCheckout();
          } catch (error) {
            // NOTHING may eat this click. Bare navigation as the last word.
            console.warn('[tk-fulfilment] checkout navigation failed, going bare:', error);
            window.location.href = '/checkout';
          }
        };
        // This used to read `.then(go, go)` — checkout proceeded whether or
        // not the write landed, on the reasoning that a dead button costs
        // more than best-effort attributes. It costs more than that: the
        // chosen day lives in this tab until the cart acks it, so a failed
        // or still-flying write sends an order with NO DAY to the counter,
        // and the shopper never learns their choice was dropped. Order #1017
        // arrived exactly that way — method and postal acked (nonce 2), the
        // day picked on screen and never written.
        //
        // So: leave only once the cart actually carries the choice. Retry
        // first, because one flaky write should not cost a sale, and say so
        // in the hint if it still will not save.
        this.#goWhenSaved(go, 2);
      },
      { capture: true, signal }
    );

    // A disabled checkout button swallows its clicks natively, which reads
    // as "checkout is broken". pointer-events:none on the disabled button
    // lets the click land on the wrapper instead, where we ALWAYS answer
    // it: a complete choice checks out (a disabled attribute under a
    // complete choice is a stale morph render, not a real lock), an
    // incomplete one pulses the hint and shows the picker.
    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;
        const ctas = event.target.closest('.cart__ctas');
        if (!ctas || event.target.closest('a, button:not([disabled]), input, select, textarea')) return;
        if (!ctas.querySelector('[data-tk-gated-checkout][disabled]')) return;
        if (this.complete) {
          event.preventDefault();
          this.#debugNote = 'wrapper click: complete → checkout';
          this.#applyGate();
          const go = () => {
            try {
              this.#goToCheckout();
            } catch {
              window.location.href = '/checkout';
            }
          };
          this.#goWhenSaved(go, 2);
          return;
        }
        this.#debugNote = 'wrapper click: incomplete → nudge';
        this.#nudgeIncomplete();
      },
      { signal }
    );

    // Belt for stale morphs: section renders from other controllers (the
    // service-line reconciler, quantity updates) can replace the checkout
    // button OUTSIDE this element, where our MutationObserver can't see it.
    // Re-assert the gate on a slow heartbeat so a stale disabled render can
    // never stick.
    this.#gateTimer = setInterval(() => {
      if (!this.#rendering) this.#applyGate();
    }, 1500);

    // Event-driven gate healing: morphs from ANY controller (our own
    // post-sync morph, the service-line reconciler adding the marker line,
    // quantity updates) re-render the pay button from Liquid, which gates
    // on cart lines that may not have landed yet — so fresh HTML arrives
    // DISABLED even though the choice is complete. Waiting for the
    // heartbeat above read as seconds of dead button after every pick;
    // watch the document for those re-renders and re-assert the gate on
    // the next frame instead. The heartbeat stays as a last-resort net.
    this.#gateObserver = new MutationObserver(() => this.#applyGateSoon());
    // Scope to the section holding the gated controls — morphs replace
    // nodes INSIDE the section wrapper, never the wrapper itself, so the
    // node stays valid across every re-render while unrelated page
    // activity (animations, lazy images) can't schedule gate passes.
    const gateRoot = document.querySelector('.cart__ctas')?.closest('.shopify-section') || document.body;
    this.#gateObserver.observe(gateRoot, {
      subtree: true,
      childList: true,
      attributeFilter: ['disabled', 'hidden'],
    });

    // Catch-up for morphs #sync skipped while a quantity update was in
    // flight. The event fires when the update STARTS (before its fetch),
    // and the skip happens while it is in flight — so wait for
    // event.promise to settle (the cart-icon pattern) before re-syncing;
    // checking at dispatch time would run before #skippedMorph is even set.
    document.addEventListener(
      'shopify:cart:lines-update',
      (event) => {
        const catchUp = () => {
          if (this.#skippedMorph) {
            this.#skippedMorph = false;
            this.#queueSync();
          }
        };
        const promise = /** @type {any} */ (event).promise;
        if (promise && typeof promise.then === 'function') promise.then(catchUp, catchUp);
        else catchUp();
      },
      { signal }
    );

    this.#initDebug();
    // Morphs are skipped while any picker field has focus; catch the summary
    // up as soon as the shopper leaves the field.
    const resyncOnBlur = () => {
      if (this.#skippedMorph) {
        this.#skippedMorph = false;
        this.#queueSync();
      }
    };
    const postal = this.querySelector('[data-tk-postal]');
    if (postal) {
      postal.addEventListener('input', () => this.#onPostalInput(postal.value), { signal });
      postal.addEventListener('blur', resyncOnBlur, { signal });
    }
    const phone = this.querySelector('[data-tk-phone]');
    if (phone instanceof HTMLInputElement) {
      phone.addEventListener('input', () => this.#onPhoneInput(phone.value), { signal });
      phone.addEventListener(
        'blur',
        () => {
          // Say what's wrong only once they've left the field — not on
          // every keystroke of a number still being typed.
          this.#showPhoneError(phone.value.trim() !== '' && !phoneValid(phone.value));
          resyncOnBlur();
        },
        { signal }
      );
    }

    // A stale selection (cutoff passed while the tab sat open, or a saved day
    // that is no longer offered) is dropped rather than trusted — here, on
    // every refresh, and once a minute while the tab stays open.
    this.#validateSavedDay();
    this.#renderAll();
    // A number the cart doesn't carry yet — the signed-in customer's account
    // phone, pre-filled — must be written before checkout, not just shown.
    if (
      this.state.method === 'Pickup' &&
      phoneValid(this.state.phone) &&
      formatPhone(this.state.phone) !== (this.dataset.phoneSaved || '')
    ) {
      this.#queueSync();
    }
    // Re-check on a slow clock, but only rebuild when the calendar actually
    // changed (a cutoff passed, midnight rolled) — a silent rebuild every
    // minute would eat keyboard focus.
    const expiryTick = () => {
      const signature = this.#calendarSignature();
      if (signature === this.#lastSignature) return;
      this.#lastSignature = signature;
      this.#validateSavedDay();
      this.#renderAll();
    };
    this.#lastSignature = this.#calendarSignature();
    this.#expiryTimer = setInterval(expiryTick, 60000);
    document.addEventListener(
      'visibilitychange',
      () => {
        if (!document.hidden) expiryTick();
      },
      { signal }
    );

    // Section morphs replace children in place without reconnecting this
    // element; re-render whenever an outside render wipes the strip.
    this.#observer = new MutationObserver(() => {
      if (this.#rendering) return;
      this.refresh();
    });
    this.#observer.observe(this, { childList: true, subtree: true });

    // Self-heal: the server-rendered dataset can be stale (bfcache, a cached
    // page, a race with another tab) and then the picker renders with no
    // selection even though the cart has one. The live cart attributes are
    // the truth — adopt them once the cart answers, unless the shopper has
    // already started choosing.
    this.#hydrateFromLiveCart();
  }

  async #hydrateFromLiveCart() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      if (this.#syncPending || this.#syncing || this.#lastSentNonce > 0) return;
      const attrs = cart.attributes || {};
      const method = String(attrs['Fulfilment'] || '');
      const dayIso = String(attrs['_tk_day_iso'] || '');
      const postal = String(attrs['Delivery postal code'] || '').toUpperCase();
      const phone = String(attrs['Phone'] || '');
      // The postal code must land in the INPUT, not just in state: refresh()
      // re-reads the input after every morph, so a value held only in memory
      // was wiped on the next redraw and then cleared server-side.
      const postalInput = this.querySelector('[data-tk-postal]');
      const adoptPostal = Boolean(postal) && !this.state.postal;
      if (adoptPostal) {
        this.state.postal = postal;
        if (postalInput instanceof HTMLInputElement && !postalInput.value.trim()) postalInput.value = postal;
      }
      // Same rule for the phone: a saved number fills an empty field.
      const phoneInput = this.querySelector('[data-tk-phone]');
      const adoptPhone = Boolean(phone) && !this.state.phone;
      if (adoptPhone) {
        this.state.phone = phone;
        if (phoneInput instanceof HTMLInputElement && !phoneInput.value.trim()) phoneInput.value = phone;
      }
      const sameChoice = method === this.state.method && dayIso === this.state.dayIso;
      if (sameChoice && !adoptPostal && !adoptPhone) return;
      this.state.method = method;
      this.state.dayIso = dayIso;
      if (this.state.postal) this.#lookupPostal(this.state.postal);
      this.#validateSavedDay();
      this.#renderAll();
      this.#applyGate();
    } catch {
      // Cart unreachable: keep the server render.
    }
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#listeners = null;
    this.#observer?.disconnect();
    this.#observer = null;
    clearTimeout(this.#syncTimer);
    clearInterval(this.#expiryTimer);
    clearInterval(this.#gateTimer);
    this.#gateObserver?.disconnect();
    this.#gateObserver = null;
    clearInterval(this.#debugTimer);
  }

  /** Re-read server-rendered state and rebuild the client-side UI. */
  refresh() {
    // Local state wins over server-rendered attributes while a local change
    // is waiting to sync — and also when the morphed-in HTML predates our
    // last confirmed write (its _tk_nonce is older than what we sent):
    // tk-service-lines morphs the same sections on its own timeline, and a
    // stale render must not wipe a selection the server already has.
    const incomingNonce = parseInt(this.dataset.nonce || '0', 10) || 0;
    const stale = this.#lastSentNonce > 0 && incomingNonce < this.#lastSentNonce;
    if (!this.#syncPending && !this.#syncing && !stale) this.#readServerState();
    // The postal input carries data-skip-node-update, so its live value
    // survives morphs and is always the freshest truth.
    const postal = this.querySelector('[data-tk-postal]');
    if (postal instanceof HTMLInputElement) {
      this.state.postal = postal.value.toUpperCase();
      this.#lookupPostal(this.state.postal);
    }
    const phone = this.querySelector('[data-tk-phone]');
    if (phone instanceof HTMLInputElement) this.state.phone = phone.value.trim();
    this.#validateSavedDay();
    this.#renderAll();
  }

  #readServerState() {
    this.config = this.#readConfig();
    this.tierMap = this.#parseTiers(this.config.tiers || '');
    this.state = {
      method: this.dataset.method || '',
      dayIso: this.dataset.dayIso || '',
      postal: (this.dataset.postal || '').toUpperCase(),
      fee: this.dataset.fee ? parseInt(this.dataset.fee, 10) : null,
      phone: this.dataset.phone || '',
    };
    // The postal input is the truth (the server-rendered value lives in it,
    // and data-skip-node-update keeps a typed value through morphs).
    const typed = this.querySelector('[data-tk-postal]');
    if (typed instanceof HTMLInputElement && typed.value.trim()) {
      this.state.postal = typed.value.trim().toUpperCase();
    }
    const typedPhone = this.querySelector('[data-tk-phone]');
    if (typedPhone instanceof HTMLInputElement && typedPhone.value.trim()) {
      this.state.phone = typedPhone.value.trim();
    }
  }

  #readConfig() {
    try {
      return JSON.parse(this.querySelector('[data-tk-fulfilment-config]')?.textContent || '{}');
    } catch {
      return {};
    }
  }

  /** "M3H M6A : 2.00" lines → { M3H: 200, M6A: 200 } */
  #parseTiers(text) {
    /** @type {Record<string, number>} */
    const map = {};
    for (const line of String(text).split('\n')) {
      const [prefixPart, pricePart] = line.split(':');
      if (!prefixPart || pricePart === undefined) continue;
      const cents = Math.round(parseFloat(pricePart.trim()) * 100);
      if (!Number.isFinite(cents)) continue;
      for (const prefix of prefixPart.trim().toUpperCase().split(/[\s,;]+/)) {
        if (prefix) map[prefix] = cents;
      }
    }
    return map;
  }

  #parseHours() {
    /** @type {Record<number, {open: number, close: number} | null>} */
    const hours = {};
    for (const line of String(this.config.hours || '').split('\n')) {
      const m = /^\s*(\d)\s*=\s*(.+?)\s*$/.exec(line);
      if (!m) continue;
      const day = parseInt(m[1], 10);
      if (/closed/i.test(m[2])) {
        hours[day] = null;
        continue;
      }
      const range = /^(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/.exec(m[2]);
      if (!range) continue;
      const open = toMinutes(range[1]);
      const close = toMinutes(range[2]);
      if (open !== null && close !== null) hours[day] = { open, close };
    }
    return hours;
  }

  /** Compact fingerprint of what the strip would show for the current method. */
  #calendarSignature() {
    if (!this.state?.method) return 'none';
    return this.#calendarDays(this.state.method)
      .map((day) => `${day.iso}:${day.available ? 1 : 0}`)
      .join(',');
  }

  #daySet(csv) {
    return new Set(
      String(csv || '')
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => n >= 0 && n <= 6)
    );
  }

  /**
   * The calendar strip: consecutive days from today. At least a week is
   * shown; the strip extends (max two weeks) until `daysShown` available
   * days are on it. Unavailable days carry a customer-readable reason.
   * @param {string} method
   * @returns {{iso: string, weekday: string, date: string, human: string, isToday: boolean, available: boolean, reason: string}[]}
   */
  #calendarDays(method) {
    const cfg = this.config;
    const strings = cfg.strings || {};
    const now = torontoNow();
    const hours = this.#parseHours();
    const allowed = this.#daySet(method === 'Delivery' ? cfg.deliveryDays : cfg.pickupDays);
    const blackouts = new Set(
      String(cfg.blackouts || '')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)
    );
    const offReason = method === 'Delivery' ? strings.noDelivery || '' : strings.noPickup || '';
    const [y, mo, d] = now.iso.split('-').map(Number);
    const baseUtc = Date.UTC(y, mo - 1, d);
    const target = cfg.daysShown || 5;
    const days = [];
    let available = 0;

    for (let offset = 0; offset < 14; offset++) {
      const date = new Date(baseUtc + offset * DAY_MS);
      const iso = date.toISOString().slice(0, 10);
      const weekdayIndex = date.getUTCDay();
      const dayHours = hours[weekdayIndex];

      let ok = true;
      let reason = '';
      if (dayHours === null || blackouts.has(iso)) {
        ok = false;
        reason = strings.closed || '';
      } else if (!allowed.has(weekdayIndex)) {
        ok = false;
        reason = offReason;
      } else if (offset === 0) {
        let cutoff = null;
        if (method === 'Delivery') {
          cutoff = toMinutes(cfg.deliveryCutoff);
          if (weekdayIndex === 5) {
            const friday = toMinutes(cfg.deliveryFridayCutoff);
            if (friday !== null) cutoff = cutoff === null ? friday : Math.min(cutoff, friday);
          }
        } else {
          cutoff = dayHours ? dayHours.close - (cfg.pickupLeadHours || 2) * 60 : null;
          if (weekdayIndex === 5) {
            const friday = toMinutes(cfg.pickupFridayCutoff);
            if (friday !== null) cutoff = cutoff === null ? friday : Math.min(cutoff, friday);
          }
        }
        if (cutoff === null || now.minutes > cutoff) {
          ok = false;
          reason = strings.pastCutoff || '';
        }
      }

      const weekday = date.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short' });
      days.push({
        iso,
        weekday: offset === 0 ? strings.today || weekday : weekday,
        dayNum: String(date.getUTCDate()),
        monthShort: date.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short' }),
        human: date.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'long', month: 'short', day: 'numeric' }),
        isToday: offset === 0,
        available: ok,
        reason,
      });
      if (ok) available += 1;
      if (offset >= 6 && available >= target) break;
    }
    return days;
  }

  #validateSavedDay() {
    if (!this.state.method || !this.state.dayIso) return;
    const stillOffered = this.#calendarDays(this.state.method).some(
      (day) => day.available && day.iso === this.state.dayIso
    );
    if (!stillOffered) {
      this.state.dayIso = '';
      this.#queueSync();
    }
  }

  /**
   * Re-render on the next frame: mutating the strip DURING a click dispatch
   * detaches the clicked button, and the drawer's outside-click check then
   * reads the click as outside and closes the drawer.
   */
  #renderSoon() {
    requestAnimationFrame(() => this.#renderAll());
  }

  /**
   * rAF-coalesced gate re-assertion for the section observer: one
   * #applyGate per frame no matter how many mutations a morph produces.
   * #applyGate's writes are value-guarded so an already-correct state
   * mutates nothing, and the drain below discards whatever records our
   * own pass did queue — the observer can never feed itself (same
   * takeRecords pattern as #renderAll).
   */
  #applyGateSoon() {
    if (this.#applyGateScheduled) return;
    this.#applyGateScheduled = true;
    requestAnimationFrame(() => {
      this.#applyGateScheduled = false;
      if (!this.#rendering) this.#applyGate();
      this.#gateObserver?.takeRecords();
    });
  }

  #selectMethod(method) {
    if (!method || this.state.method === method) return;
    this.state.method = method;
    this.state.dayIso = '';
    if (method === 'Delivery' && this.state.postal) this.#lookupPostal(this.state.postal);
    this.#renderSoon();
    this.#queueSync(60);
  }

  #selectDay(iso) {
    this.#saveError = false;
    this.state.dayIso = iso;
    this.#renderSoon();
    this.#queueSync(60);
  }

  #onPhoneInput(value) {
    this.#saveError = false;
    this.state.phone = value.trim();
    // A fixed number clears its error at once; a wrong one waits for blur.
    if (phoneValid(value)) this.#showPhoneError(false);
    this.#applyGate();
    this.#queueSync();
  }

  #showPhoneError(show) {
    for (const error of this.querySelectorAll('[data-tk-phone-error]')) error.hidden = !show;
    const input = this.querySelector('[data-tk-phone]');
    if (input) input.setAttribute('aria-invalid', String(show));
  }

  #onPostalInput(value) {
    this.state.postal = value.toUpperCase();
    this.#lookupPostal(this.state.postal);
    if (this.state.fee === null) {
      this.state.dayIso = '';
    }
    this.#renderSoon();
    this.#queueSync();
  }

  #lookupPostal(postal) {
    const fsa = postal.replace(/\s+/g, '').slice(0, 3);
    if (/^[A-Z]\d[A-Z]$/.test(fsa) && this.tierMap[fsa] !== undefined) {
      this.state.fee = this.tierMap[fsa];
    } else {
      this.state.fee = null;
    }
  }

  #renderAll() {
    this.#rendering = true;
    try {
      this.#render();
      this.#applyGate();
    } finally {
      // Draining synchronously discards exactly our own records (queued
      // during this task) and re-arms immediately, so a genuine external
      // morph a moment later is never swallowed.
      this.#observer?.takeRecords();
      this.#rendering = false;
    }
  }

  #render() {
    const { method, postal, fee, dayIso } = this.state;
    const strings = this.config.strings || {};

    for (const button of this.querySelectorAll('[data-tk-method]')) {
      const selected = button.dataset.tkMethod === method;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    }
    for (const panel of this.querySelectorAll('[data-tk-panel]')) {
      panel.hidden = panel.dataset.tkPanel !== method;
    }
    for (const ghost of this.querySelectorAll('[data-tk-ghost]')) {
      ghost.hidden = Boolean(method);
    }
    if (!method) return;

    const panel = this.querySelector(`[data-tk-panel="${method}"]`);
    if (!panel) return;

    if (method === 'Delivery') {
      const priceLine = panel.querySelector('[data-tk-price-line]');
      const errorLine = panel.querySelector('[data-tk-postal-error]');
      const days = panel.querySelector('[data-tk-days]');
      const freeNote = panel.querySelector('[data-tk-free-note]');
      const complete = postal.replace(/\s+/g, '').length >= 3;
      const found = fee !== null;

      if (priceLine) {
        priceLine.hidden = !found;
        if (found) {
          priceLine.textContent = (strings.priceLine || '{postal} — {price}')
            .replace('{postal}', postal.trim())
            .replace('{price}', formatMoney(fee));
        }
      }
      if (errorLine) errorLine.hidden = !complete || found;
      if (days) days.hidden = !found;
      if (freeNote) {
        const threshold = (this.config.freeThreshold || 0) * 100;
        const minFee = (this.config.freeMinFee || 0) * 100;
        const show = found && threshold > 0 && fee >= minFee;
        freeNote.hidden = !show;
        if (show) {
          freeNote.textContent = (strings.freeNote || '').replace('{threshold}', formatMoney(threshold));
        }
      }
      if (found) this.#renderDays(panel, 'Delivery', dayIso);
    } else {
      this.#renderDays(panel, 'Pickup', dayIso);
    }
  }

  #renderDays(panel, method, selectedIso) {
    const list = panel.querySelector('[data-tk-day-list]');
    if (!list) return;
    const days = this.#calendarDays(method);
    this.#dayByIso = new Map(days.map((day) => [day.iso, day.human]));

    // Keyboard focus must survive the rebuild — remember which card had it.
    const active = document.activeElement;
    const focusedIso = active instanceof HTMLElement && list.contains(active) ? active.dataset.tkDayIso : null;

    list.textContent = '';

    if (!days.some((day) => day.available)) {
      const note = document.createElement('p');
      note.className = 'tk-fulfilment__note tk-fulfilment__no-days';
      note.textContent = this.config.strings?.noDays || '';
      list.append(note);
      return;
    }
    let soonestIso = '';
    for (const day of days) {
      if (day.available) {
        soonestIso = day.iso;
        break;
      }
    }
    for (const day of days) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'tk-fulfilment__day';
      card.classList.toggle('is-selected', day.available && day.iso === selectedIso);
      card.classList.toggle('is-off', !day.available);
      card.classList.toggle('is-today', day.isToday);
      card.disabled = !day.available;
      card.setAttribute('aria-pressed', String(day.available && day.iso === selectedIso));
      // Choice-card grammar: small-caps weekday, big date numeral, tiny
      // month marker. The label carries the full date PLUS the visible
      // reason / soonest tag — an aria-label replaces the content as the
      // accessible name, so anything not in it goes unannounced.
      let label = day.human;
      if (!day.available && day.reason) label += `, ${day.reason}`;
      else if (day.iso === soonestIso && this.config.strings?.soonest) label += `, ${this.config.strings.soonest}`;
      card.setAttribute('aria-label', label);

      const weekday = document.createElement('span');
      weekday.className = 'tk-fulfilment__day-dow';
      weekday.textContent = day.weekday;
      const num = document.createElement('span');
      num.className = 'tk-fulfilment__day-num';
      num.textContent = day.dayNum;
      const month = document.createElement('span');
      month.className = 'tk-fulfilment__day-mo';
      month.textContent = day.monthShort;
      card.append(weekday, num, month);
      // The date stays put; the reason joins it, so the strip still reads as
      // a calendar even where days are closed. The first bookable day wears
      // the SOONEST tag so the fastest option answers itself.
      if (!day.available && day.reason) {
        const reason = document.createElement('small');
        reason.className = 'tk-fulfilment__day-why';
        reason.textContent = day.reason;
        card.append(reason);
      } else if (day.iso === soonestIso && this.config.strings?.soonest) {
        const soon = document.createElement('span');
        soon.className = 'tk-fulfilment__day-soon';
        soon.textContent = this.config.strings.soonest;
        card.append(soon);
      }

      if (day.available) {
        card.dataset.tkDayIso = day.iso;
        card.addEventListener(
          'click',
          (event) => {
            event.stopPropagation();
            this.#selectDay(day.iso);
          },
          { signal: this.#listeners?.signal }
        );
      }
      list.append(card);
    }

    if (focusedIso) {
      const again = list.querySelector(`[data-tk-day-iso="${focusedIso}"]`);
      if (again instanceof HTMLElement) again.focus({ preventScroll: true });
    }
  }

  /**
   * Checkout only once the cart carries the choice. Retries a stalled or
   * failed write before giving up, then holds rather than sending an order
   * whose day exists only in this browser tab.
   */
  #goWhenSaved(go, attemptsLeft) {
    if (this.#acked) {
      this.#saveError = false;
      go();
      return;
    }
    if (attemptsLeft <= 0) {
      this.#saveError = true;
      this.#debugNote = 'checkout held: the choice never reached the cart';
      console.warn('[tk-fulfilment] holding checkout: the cart never acked the choice');
      this.#applyGate();
      this.#nudgeIncomplete();
      return;
    }
    const again = () => this.#goWhenSaved(go, attemptsLeft - 1);
    this.#flushSync().then(again, again);
  }

  /**
   * Whether the choice is complete enough to check out: a method, a day, a
   * priced postal code for delivery (the address itself is checkout's job),
   * and a phone number for pickup. Checkout can't require a phone on pickup
   * orders, so this gate is the only place that can; delivery's comes with
   * the checkout address.
   */
  get complete() {
    return this.#choiceComplete && !this.#onlyPhoneMissing;
  }

  /** Method and day (and a priced postal code) — everything but the phone. */
  get #choiceComplete() {
    const { method, dayIso, fee } = this.state;
    if (!method || !dayIso) return false;
    return method === 'Pickup' || fee !== null;
  }

  /** A pickup missing only its phone number: the hint and the nudge point at it. */
  get #onlyPhoneMissing() {
    return this.#choiceComplete && this.state.method === 'Pickup' && !phoneValid(this.state.phone);
  }

  /**
   * Enables/disables the checkout buttons. Liquid renders the initial gate
   * from cart attributes; this keeps it live between server round-trips.
   * Document-wide on purpose: the page cart and the drawer share one state.
   * The gate only opens once the server has ACKED the attributes — checkout
   * must never proceed with a choice the cart doesn't actually carry.
   */
  #applyGate() {
    // Self-healing: if something client-side has removed the pay button
    // (observed in the wild: a summary area with no gated button in it),
    // put a working one back. An anchor: even with every script dead after
    // this, its href still reaches checkout.
    const ctas = document.querySelector('.cart__ctas');
    if (ctas && !ctas.querySelector('[data-tk-gated-checkout], .tk-minicart-checkout')) {
      const replacement = document.createElement('a');
      replacement.className = 'button cart__checkout-button';
      replacement.setAttribute('data-tk-gated-checkout', '');
      replacement.setAttribute('data-tk-injected', '');
      replacement.href = `${(window.Shopify?.routes?.root || '/').replace(/\/$/, '')}/checkout`;
      const label = document.createElement('span');
      label.className = 'button-text';
      label.textContent = this.config.strings?.payLabel || 'Continue to payment';
      replacement.append(label);
      ctas.prepend(replacement);
      this.#debugNote = 'pay button was missing — injected a replacement';
    }
    // Never two pay controls: if the real one is (back) in the DOM, retire
    // any injected stand-in.
    if (document.querySelector('[data-tk-gated-checkout]:not([data-tk-injected])')) {
      for (const injected of document.querySelectorAll('[data-tk-gated-checkout][data-tk-injected]')) {
        injected.remove();
      }
    }

    // The button disables only while the choice is truly incomplete. During
    // the brief save window it stays clickable — the click intercept flushes
    // the save first — so checkout never feels dead.
    const gate = !this.complete;
    const saving = this.complete && !this.#acked;
    const savingText = this.config.strings?.saving || '';
    const checkoutHref = `${(window.Shopify?.routes?.root || '/').replace(/\/$/, '')}/checkout`;
    for (const button of document.querySelectorAll('[data-tk-gated-checkout]')) {
      const off = gate;
      button.toggleAttribute('disabled', off);
      // `disabled` does nothing on an anchor, and the pointer-events rule that
      // dresses it stops a mouse but not a keyboard — Tab, Enter, and the
      // browser follows the href to checkout. So take the href away while the
      // gate is shut: with no href there is nothing to activate, by any input,
      // and nothing to open in a new tab either. Value-guarded, because the
      // gate observer watches these nodes.
      if (off) {
        if (button.hasAttribute('href')) {
          button.dataset.tkHref = button.getAttribute('href') || '';
          button.removeAttribute('href');
        }
      } else if (button.tagName === 'A' && !button.hasAttribute('href')) {
        button.setAttribute('href', button.dataset.tkHref || checkoutHref);
      }
      // disabled is inert on an <a>; aria-disabled is what assistive tech reads.
      if (button.getAttribute('aria-disabled') !== String(off)) button.setAttribute('aria-disabled', String(off));
    }
    // Every write below is value-guarded: the gate observer watches these
    // nodes, and an applyGate pass over an already-correct state must
    // queue zero mutation records or the observer would re-schedule
    // itself indefinitely.
    for (const hint of document.querySelectorAll('[data-tk-gate-hint]')) {
      const hideHint = !gate && !saving && !this.#saveError;
      if (hint.hidden !== hideHint) hint.hidden = hideHint;
      const failText = this.config.strings?.saveFailed || "We couldn't save your choice just now \u2014 please tap again.";
      let hintText = this.config.strings?.hint || hint.textContent || '';
      if (this.#onlyPhoneMissing) hintText = this.config.strings?.hintPhone || hintText;
      if (this.#saveError) hintText = failText;
      else if (saving && savingText) hintText = savingText;
      if (hint.textContent !== hintText) hint.textContent = hintText;
    }
    for (const confirm of document.querySelectorAll('[data-tk-gate-confirm]')) {
      const hideConfirm = gate || saving;
      if (confirm.hidden !== hideConfirm) confirm.hidden = hideConfirm;
      if (!gate && !saving) {
        const confirmText = this.#confirmText();
        if (confirm.textContent !== confirmText) confirm.textContent = confirmText;
      }
    }
  }

  #confirmText() {
    const strings = this.config.strings || {};
    const human = this.#dayByIso.get(this.state.dayIso) || this.state.dayIso;
    if (this.state.method === 'Delivery') {
      return (strings.confirmDelivery || 'Delivery — {day} · {price}')
        .replace('{day}', human)
        .replace('{postal}', this.state.postal.trim())
        .replace('{price}', formatMoney(this.state.fee ?? 0));
    }
    return (strings.confirmPickup || 'Pickup — {day}').replace('{day}', human);
  }

  /**
   * Kicks off the real checkout submit. The clicked button can be a node a
   * section morph already replaced (requestSubmit throws on a submitter the
   * form doesn't own), the form can be missing, or a native submit can be
   * silently cancelled by an invalid hidden control — every one of those
   * paths ends in a direct navigation to checkout instead of a dead click.
   * The cart itself (items, attributes, note) already lives server-side, so
   * the navigation fallback loses nothing.
   */
  /**
   * The postal code rides to checkout as a prefill (cart-permalink parameter
   * names) so the shopper types it once. Everything else about the address
   * is Shopify's own checkout form's job — it autofills, validates, and
   * stores to the customer account, none of which a theme field can do.
   */
  #prefillFields() {
    const { method, postal } = this.state;
    const zip = postal.trim();
    if (method !== 'Delivery' || !zip) return null;
    return { 'checkout[shipping_address][zip]': zip };
  }

  /**
   * One deterministic road to payment: a plain navigation to /checkout with
   * the prefill in the query string. The cart (items, attributes, note)
   * already lives server-side, so a form submit adds nothing — and the
   * form-vs-fallback race it created was itself a way to lose the click.
   */
  /** Visible answer to a click the gate refuses: flash the hint, show what's missing. */
  #nudgeIncomplete() {
    for (const hint of document.querySelectorAll('[data-tk-gate-hint]')) {
      hint.hidden = false;
      hint.classList.remove('tk-gate__hint--pulse');
      void hint.offsetWidth;
      hint.classList.add('tk-gate__hint--pulse');
    }
    const phone = this.querySelector('[data-tk-phone]');
    if (this.#onlyPhoneMissing && phone instanceof HTMLInputElement) {
      if (phone.value.trim()) this.#showPhoneError(true);
      phone.scrollIntoView({ behavior: 'smooth', block: 'center' });
      phone.focus({ preventScroll: true });
      return;
    }
    const scrollTarget = this.querySelector('.tk-fulfilment__toggle') || this;
    scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * On-page diagnostics: add &tk_debug=1 to the URL and a live status strip
   * appears — module alive, gate state, saved fields, what the last click
   * did. A screenshot of it answers every "the button does nothing" report.
   */
  #initDebug() {
    // The preview_theme_id redirect strips query params, so accept a #hash
    // too (redirects preserve fragments) and stick for the session.
    let on = /tk_debug/.test(window.location.search + window.location.hash);
    try {
      if (on) sessionStorage.setItem('tk_debug', '1');
      else on = sessionStorage.getItem('tk_debug') === '1';
    } catch {
      // storage blocked — the URL trigger alone decides
    }
    if (!on) return;
    // One global panel, reused across morph-reconnected instances.
    let panel = document.getElementById('tk-debug-panel');
    if (!panel) {
      panel = document.createElement('div');
      panel.id = 'tk-debug-panel';
      panel.style.cssText =
        'position:fixed;left:8px;bottom:8px;z-index:2147483647;background:#241f14;color:#fff;' +
        'font:13px/1.5 ui-monospace,monospace;padding:10px 14px;border-radius:8px;max-width:92vw;pointer-events:none;';
      const text = document.createElement('div');
      text.setAttribute('data-tk-debug-text', '');
      text.style.whiteSpace = 'pre-wrap';
      const link = document.createElement('a');
      link.textContent = '→ tap here: open checkout directly';
      link.href = `${(window.Shopify?.routes?.root || '/').replace(/\/$/, '')}/checkout`;
      link.style.cssText =
        'display:block;margin-top:6px;color:#f5d78e;text-decoration:underline;pointer-events:auto;font-weight:700;';
      panel.append(text, link);
      document.body.append(panel);
    }
    clearInterval(this.#debugTimer);
    this.#debugTimer = setInterval(() => {
      const text = document.querySelector('[data-tk-debug-text]');
      if (!text) return;
      const { method, dayIso, postal, fee, phone } = this.state;
      const button = document.querySelector('[data-tk-gated-checkout]');
      text.textContent =
        `TK debug ${new Date().toTimeString().slice(0, 8)}\n` +
        `complete:${this.complete}  acked:${this.#acked}  failures:${this.#failures}\n` +
        `method:${method || '—'}  day:${dayIso || '—'}  phone:${phoneValid(phone) ? 'ok' : phone ? 'invalid' : '—'}\n` +
        `postal:${postal || '—'}  fee:${fee === null ? '—' : fee}\n` +
        `button:${button ? (button.hasAttribute('disabled') ? 'DISABLED' : 'enabled') : 'MISSING'}\n` +
        `dom: ctas:${document.querySelectorAll('.cart__ctas').length}` +
        ` card:${document.querySelectorAll('.cart-summary').length}` +
        ` tkorder:${document.querySelectorAll('.cart-page--tk-order').length}` +
        ` pickers:${document.querySelectorAll('tk-fulfilment').length}` +
        ` buttons:${document.querySelectorAll('button').length}\n` +
        `page:${window.location.pathname}\n` +
        `last click:${this.#debugNote || '—'}\n` +
        `ctas html: ${(document.querySelector('.cart__ctas')?.outerHTML || '(none)').replace(/\s+/g, ' ').slice(0, 320)}`;
    }, 500);
  }

  /**
   * There are no shipping rates on this store — the hidden tk-delivery-fee
   * line IS the delivery charge, added by the service-line controller. Give
   * it a beat to land before navigating; proceed regardless after ~2s
   * (never dead-end a purchase — the server-rendered gate also refuses a
   * fee attribute with no matching fee line).
   */
  /**
   * The cart's method-marker lines are the checkout guard: the fee line
   * (delivery only) disables the Pickup side via inventory, and the $0
   * pickup marker (pickup only) disables the Ship side via its rateless
   * shipping profile. Both are written by the service-line controller a beat
   * after the attributes save — navigating before they settle would hand
   * checkout a cart whose tabs don't match the choice. Wait for the exact
   * expected state in BOTH directions; proceed regardless after ~3 s (never
   * dead-end a purchase — the Liquid gate re-checks server-side too).
   */
  /** tk_fee_as_line, read from the config JSON tk-cart-config renders. Defaults on. */
  #feeAsLine() {
    const nodes = document.querySelectorAll('[data-tk-cart-config]');
    const node = nodes[nodes.length - 1];
    if (!node) return true;
    try {
      return JSON.parse(node.textContent || '').fee?.enabled !== false;
    } catch {
      return true;
    }
  }

  async #awaitMarkerLines() {
    try {
      const { method, fee } = this.state;
      if (method !== 'Delivery' && method !== 'Pickup') return;
      // With fee-as-line off the reconciler clears fee lines instead of
      // adding them, so the expected fee total is zero and there is
      // nothing to wait for.
      const wantFee = method === 'Delivery' && this.#feeAsLine() ? fee || 0 : 0;
      const wantMarker = method === 'Pickup';
      for (let attempt = 0; attempt < 8; attempt += 1) {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        let feeCents = 0;
        let markerCount = 0;
        for (const item of cart.items || []) {
          if (item.handle === 'tk-delivery-fee') feeCents += item.original_line_price;
          if (item.handle === 'tk-pickup-marker') markerCount += item.quantity;
        }
        if (feeCents === wantFee && (markerCount > 0) === wantMarker) return;
        document.dispatchEvent(new CustomEvent('tk:fulfilment-synced'));
        await new Promise((resolve) => setTimeout(resolve, 400));
      }
    } catch {
      /* best-effort — the purchase must never dead-end here */
    }
  }

  async #goToCheckout() {
    this.#debugNote = 'navigating to checkout';
    await this.#awaitMarkerLines();
    // Freshly sliced lines get their weight written on ("Total weight: 2 lb")
    // for checkout and the order — tk-service-lines.js. Never longer than 3 s:
    // the label is a nicety, the purchase is not.
    try {
      await Promise.race([window.tkStampSlicedLines?.(), new Promise((resolve) => setTimeout(resolve, 3000))]);
    } catch {
      /* go without the label */
    }
    const root = window.Shopify?.routes?.root || '/';
    let url = `${root.replace(/\/$/, '')}/checkout`;
    const fields = this.#prefillFields();
    if (fields) {
      const params = new URLSearchParams();
      for (const [name, value] of Object.entries(fields)) params.set(name, value);
      url += `?${params.toString()}`;
    }
    // Test seam: the harness records the navigation instead of unloading.
    if (typeof window.__tkNavigate === 'function') window.__tkNavigate(url);
    else window.location.assign(url);
  }

  #queueSync(delay = 400) {
    this.#acked = false;
    this.#syncPending = true;
    clearTimeout(this.#syncTimer);
    this.#syncTimer = setTimeout(() => this.#sync(), delay);
  }

  /** Run any pending sync now and wait for it to settle (bounded). */
  async #flushSync() {
    clearTimeout(this.#syncTimer);
    if (this.#syncing) {
      this.#syncQueued = true;
    } else if (this.#syncPending || !this.#acked) {
      await this.#sync();
    }
    const start = Date.now();
    while ((this.#syncing || this.#syncQueued) && Date.now() - start < 5000) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  async #sync() {
    if (this.#syncing) {
      this.#syncQueued = true;
      return;
    }
    // In the theme editor, render the UI but never write to the merchant's
    // cart or morph sections the editor owns.
    if (window.Shopify?.designMode) {
      this.#acked = true;
      this.#syncPending = false;
      this.#applyGate();
      return;
    }
    this.#syncing = true;
    this.#syncPending = false;

    const { method, dayIso, postal, fee, phone } = this.state;
    this.#nonce += 1;
    this.#lastSentNonce = this.#nonce;
    const delivery = method === 'Delivery';
    // The address lives on the order itself (checkout collects it); these
    // attributes carry only what checkout cannot know — the chosen day, the
    // priced zone, and the pickup phone number checkout won't require.
    // Blank values clear stale ones from older carts (pickup used to carry
    // a time window too).
    const attributes = {
      'Fulfilment': method || '',
      'Fulfilment day': (dayIso && this.#dayByIso.get(dayIso)) || '',
      '_tk_day_iso': dayIso || '',
      'Phone': method === 'Pickup' && phoneValid(phone) ? formatPhone(phone) : '',
      'Pickup time': '',
      '_tk_time': '',
      'Delivery postal code': delivery ? postal.trim() : '',
      '_tk_delivery_fee': delivery && fee !== null ? String(fee) : '',
      'Delivery address': '',
      'Delivery contact': '',
      'Delivery notes': '',
      '_tk_address1': '',
      '_tk_address2': '',
      '_tk_city': '',
      '_tk_first': '',
      '_tk_last': '',
      '_tk_phone': '',
      '_tk_nonce': String(this.#nonce),
    };

    const sectionIds = new Set();
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    }

    try {
      const response = await fetch(`${theme().routes?.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes,
          sections: Array.from(sectionIds).join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) throw new Error(`cart update failed: ${response.status}`);
      const cart = await response.json();
      this.#failures = 0;
      this.#saveError = false;
      if (!this.#syncPending && !this.#syncQueued) this.#acked = true;
      this.#applyGate();
      // The service-line controller follows the choice — fee or marker
      // line, and the day note on it (attribute writes don't fire
      // cartLinesUpdate).
      document.dispatchEvent(new CustomEvent('tk:fulfilment-synced'));
      if (cart.sections) {
        const active = document.activeElement;
        const typing = active instanceof Element && active.matches('[data-tk-postal], [data-tk-phone]') && this.contains(active);
        // A quantity update in flight means our sections HTML carries the
        // PRE-change line data — morphing it in would show stale
        // quantities and totals until the next round-trip.
        const busyItems = document.querySelector('cart-items-component.cart-items-disabled');
        for (const [sectionId, html] of Object.entries(cart.sections)) {
          if (typeof html !== 'string') continue;
          // While a picker field has focus or the item list is mid-
          // update, skip the morph — the blur handler / lines-update
          // listener re-syncs so the summary always catches up.
          if (typing || busyItems) {
            this.#skippedMorph = true;
          } else {
            await morphSection(sectionId, html, { mode: morphModeFor(sectionId) });
          }
        }
        // The morphs above re-render the pay button from Liquid, possibly
        // disabled (the marker/fee line hasn't landed in the cart yet) —
        // re-assert the acked gate now instead of waiting for the heartbeat.
        this.#applyGate();
      }
    } catch (error) {
      console.warn('[tk-fulfilment] attribute sync failed:', error);
      // Gate stays closed (not acked); retry a few times, then wait for the
      // next user interaction rather than hammering a dead connection.
      this.#failures = (this.#failures || 0) + 1;
      if (this.#failures <= 3) {
        this.#syncPending = true;
        clearTimeout(this.#syncTimer);
        this.#syncTimer = setTimeout(() => this.#sync(), 2500);
      }
    } finally {
      this.#syncing = false;
      if (this.#syncQueued) {
        this.#syncQueued = false;
        this.#sync();
      }
    }
  }
}

if (!customElements.get('tk-fulfilment')) {
  customElements.define('tk-fulfilment', TkFulfilment);
}
